import React from 'react';
import { FieldRow } from '../shipping/FieldRow';

interface DeliveryNoteUploadSectionProps {
  t: (key: string) => string;
  isEditMode: boolean;
  deliveryNoteImageUrl: string | null;
  onViewImage: () => void;
  deliveryNoteImage: File | null;
  uploading: boolean;
  pendingSaved: boolean;
  uploadError: string;
  deliveryNoteJson: any;
  onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUploadImage: () => void;
  onSavePending: () => void;
  onFinalize: () => void;
  onCancel: () => void;
}

const DeliveryNoteUploadSection: React.FC<DeliveryNoteUploadSectionProps> = ({
  t,
  isEditMode,
  deliveryNoteImageUrl,
  onViewImage,
  deliveryNoteImage,
  uploading,
  pendingSaved,
  uploadError,
  deliveryNoteJson,
  onImageChange,
  onUploadImage,
  onSavePending,
  onFinalize,
  onCancel
}) => {
  return (
    <>
      <FieldRow label={t('delivery.note.image')}>
        <div className="d-flex align-items-center gap-2">
          {isEditMode && deliveryNoteImageUrl && (
            <button
              type="button"
              className="btn btn-outline-secondary p-1"
              style={{ border: 'none', background: 'none', verticalAlign: 'middle' }}
              onClick={onViewImage}
              title={t('view.image')}
            >
              <span role="img" aria-label="View Image" style={{ fontSize: '1.2em', verticalAlign: 'middle' }}>🖼️</span>
            </button>
          )}
          {!isEditMode && (
            <>
              <div className="input-group mb-2">
                <input type="file" accept="image/*" className="form-control" onChange={onImageChange} disabled={pendingSaved} />
                <button type="button" className="btn btn-outline-secondary"
                  onClick={onUploadImage}
                  disabled={!deliveryNoteImage || uploading || pendingSaved}>
                  {uploading ? t('uploading') : t('upload.process')}
                </button>
              </div>
              {uploadError && (
                <div className="alert alert-danger mb-2">{uploadError}</div>
              )}
            </>
          )}
        </div>
      </FieldRow>

      {/* Delivery Note Matches Section */}
      {!isEditMode && deliveryNoteJson && (
        <FieldRow label="">
          <div className="mb-4">
            {deliveryNoteJson.matches && (
              <div className="accordion mb-2" id="foundMatchesAccordion">
                <div className="accordion-item">
                  <h6 className="accordion-header" id="foundMatchesHeading">
                    <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#foundMatchesCollapse" aria-expanded="true" aria-controls="foundMatchesCollapse">
                      <strong>{t('found.matches')}:</strong>
                    </button>
                  </h6>
                  <div id="foundMatchesCollapse" className="accordion-collapse collapse show" aria-labelledby="foundMatchesHeading" data-bs-parent="#foundMatchesAccordion">
                    <div className="accordion-body">
                      {deliveryNoteJson.matches.vehicle && (
                        <div className="mb-1">
                          <strong>{t('vehicle')}:</strong> {deliveryNoteJson.matches.vehicle.licensePlate}
                          ({deliveryNoteJson.matches.vehicle.brand} 
                          {deliveryNoteJson.matches.vehicle.model})
                          <span className="badge bg-secondary ms-2">{deliveryNoteJson.matches.vehicle.matchType}</span>
                        </div>
                      )}
                      {deliveryNoteJson.matches.driver && (
                        <div className="mb-1">
                          <strong>{t('driver')}:</strong> {deliveryNoteJson.matches.driver.name}
                          ({deliveryNoteJson.matches.driver.phone})
                          <span className="badge bg-secondary ms-2">{deliveryNoteJson.matches.driver.matchType}</span>
                        </div>
                      )}
                      {deliveryNoteJson.matches.address && (
                        <div className="mb-1">
                          <strong>{t('address')}:</strong>
                          {deliveryNoteJson.matches.address.district && (
                            <span> {deliveryNoteJson.matches.address.district.name}, </span>
                          )}
                          {deliveryNoteJson.matches.address.province && (
                            <span>{deliveryNoteJson.matches.address.province.name}</span>
                          )}
                          <span className="badge bg-secondary ms-2">{deliveryNoteJson.matches.address.matchType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Raw JSON (collapsible) */}
            <div className="accordion">
              <div className="accordion-item">
                <h6 className="accordion-header">
                  <button className="accordion-button collapsed"
                    type="button" data-bs-toggle="collapse"
                    data-bs-target="#rawJsonCollapse">
                    {t('raw.processed.data')}
                  </button>
                </h6>
                <div id="rawJsonCollapse" className="accordion-collapse collapse">
                  <div className="accordion-body">
                    <pre style={{ fontSize: '0.8em', maxHeight: '300px', overflow: 'auto' }}>
                      {JSON.stringify(deliveryNoteJson, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Show Save button after upload & process */}
            {!pendingSaved && (
              <div className="d-flex gap-2 mt-3">
                <button type="button" className="btn btn-success" onClick={onSavePending}>
                  {t('save')} (Pending)
                </button>
                <button type="button" className="btn btn-secondary" onClick={onCancel}>
                  {t('cancel')}
                </button>
              </div>
            )}
          </div>
        </FieldRow>
      )}
              
      {/* Show finalize button after save */}
      {pendingSaved && (
        <FieldRow label="">
          <div>
            <div className="alert alert-info">{t('record.saved.pending')} <b>{t('review.finalize.below')}</b>.</div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={onFinalize}>
                {t('create.shipping.record')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </FieldRow>
      )}
    </>
  );
};

export default DeliveryNoteUploadSection;
